import { useState, useEffect } from 'react';
import { useModalA11y } from '../hooks/use-modal-a11y';

interface PauseReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  orderNumber?: string;
  isSubmitting?: boolean;
  defaultPauseTimeoutHours?: number;
}

export function PauseReasonModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  orderNumber,
  isSubmitting = false,
  defaultPauseTimeoutHours = 72
}: PauseReasonModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required field
    if (!reason.trim()) {
      setError('Please provide a reason for pausing this order');
      return;
    }

    // Submit the reason
    onSubmit(reason.trim());
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, handleClose);

  if (!isOpen) return null;

  return (
    <div data-testid="pause-reason-modal" className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity ${
          isSubmitting ? 'cursor-wait' : 'cursor-default'
        }`}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pause-reason-modal-title"
          tabIndex={-1}
          className="relative bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full p-6 focus:outline-none"
        >
          {/* Loading Overlay */}
          {isSubmitting && (
            <div className="absolute inset-0 bg-white dark:bg-secondary-800 bg-opacity-75 rounded-lg flex items-center justify-center z-10">
              <div className="text-center">
                <i className="fa-solid fa-spinner fa-spin text-3xl text-yellow-600 mb-2"></i>
                <p className="text-sm text-gray-600 dark:text-secondary-400">Pausing workflow...</p>
              </div>
            </div>
          )}
          
          {/* Header */}
          <div className="mb-4">
            <h3 id="pause-reason-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Pause Workflow
            </h3>
            {orderNumber && (
              <p className="mt-1 text-sm text-gray-600 dark:text-secondary-400">
                Order: {orderNumber}
              </p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="pause-reason" className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                Reason for pausing <span className="text-red-500">*</span>
              </label>
              <textarea
                data-testid="pause-reason-input"
                id="pause-reason"
                rows={4}
                className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-secondary-900 text-gray-900 dark:text-secondary-100 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                  error ? 'border-red-500' : 'border-gray-300 dark:border-secondary-600'
                }`}
                placeholder="Please explain why this order needs to be paused..."
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError('');
                }}
                disabled={isSubmitting}
                autoFocus
              />
              {error && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 mb-4">
              <div className="flex">
                <i className="fa-solid fa-info-circle text-yellow-600 mt-0.5 mr-2"></i>
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">What happens when paused?</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>The workflow will stop processing at its current step</li>
                    <li>No timeout will occur while paused</li>
                    <li>You can resume the workflow at any time</li>
                    <li>Auto-resume will occur after {defaultPauseTimeoutHours} hours if not manually resumed</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-secondary-600 rounded-md text-gray-700 dark:text-secondary-200 hover:bg-gray-50 dark:hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                data-testid="pause-reason-submit"
                type="submit"
                className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Pausing...' : 'Pause Workflow'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
